
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { localize } from '@/utils/i18n-helpers';
import { Debounce, cancelDebounce } from '@/utils/decorators';
import { MEMBERSHIP_PROVIDERS } from '@/utils/constants';
import { PERMISSION } from '@/permissions';
import TextButton from '@/components/shared/suites/TextButton.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import SlideFadeTransition from '@/components/shared/transition/SlideFadeTransition.vue';
import OrderMenu from './OrderMenu.vue';
import OrderMenuCard from './OrderMenuCard.vue';
import ScrollableButtons from '@/components/shared/ScrollableButtons.vue';
import SearchResults from './SearchResults.vue';
import MembershipModal from '@/components/orders/modals/MembershipModal.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import TextField from '@/components/shared/TextField.vue';
import OpenItemModal from '@/components/orders/modals/OpenItemModal.vue';

const namespace: string = 'orders';

@Component<OrderMenus>({
	components: {
		TextField,
		IconButton,
		OrderMenu,
		SuitesHeader,
		WarningModal,
		OrderMenuCard,
		SearchResults,
		SlideFadeTransition,
		ScrollableButtons,
		MembershipModal,
		InfoBanner,
		TextButton,
		OpenItemModal
	}
})
export default class OrderMenus extends Vue {
	@Prop({ type: Array, required: true }) private menus!: Menu[];
	@Prop({ type: Boolean, default: false }) private kioskMode!: boolean;
	@Watch('menus') onMenusChange(): void {
		// Select the first non-member-only menu when the menus change
		// Unless the menu changes are caused by applying the membership
		this.$nextTick(() => {
			if (!this.showMembershipModal) {
				this.resetMenuSelection();
			}
		});
	}
	@Action('fetchNextOrderQueueTime', { namespace }) private fetchNextOrderQueueTime!: (specifier?: string) => void;
	@Action('fetchRestaurant', { namespace: 'auth' }) private fetchRestaurant!: (params: { tenantId: string }) => Promise<void>;
	@Getter('getCartItemsCount', { namespace }) private cartItemsCount!: number;
	@Getter('getModifyingOrder', { namespace }) private modifyingOrder!: boolean;
	@Getter('getOrderDetails', { namespace }) private orderDetails!: OrderDetails;
	@Getter('getCheckoutInfo', { namespace }) checkout!: Checkout;
	@Getter('getQueueInfo', { namespace }) private queueInfo!: QueueInfo | null;
	@Getter('isQuoting', { namespace }) private isQuoting!: boolean;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('areOrdersPaused', { namespace: 'auth' }) private ordersPaused!: boolean;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('isCateringRestaurant', { namespace: 'auth' }) private isCateringRestaurant!: boolean;
	@Getter('isPizzabotLocation', { namespace: 'auth' }) private isPizzabotLocation!: boolean;
	@Getter('getEmployeeName', { namespace: 'auth' }) private employeeName!: string | null;
	@Watch('menuSelectedIndex') onMenuSelectedIndexChange(newIndex: number, prevIndex: number): void {
		this.prevMenuSelectedIndex = prevIndex;
	}

	$refs!: { orderMenu: OrderMenu[]; };

	private menuSelectedIndex: number = 0;
	private prevMenuSelectedIndex: number = this.menuSelectedIndex;
	private sectionsHeaderHeight: number = 0;
	private ordersPausedInterval: number | null = null;
	private title: string = '';
	private searchInput: string = '';
	private showScrollToTopButton: boolean = false;
	private showMobileSearch: boolean = false;
	private searchResultItems: MenuItem[] = [];

	// Warning modal
	private warningModalTitle: string = '';
	private warningModalBody: string = '';
	private showWarningModal: boolean = false;
	private showMembershipModal: boolean = false;
	private showOpenItemModal: boolean = false;

	/**
	 * Name of the operator the tab will belong to once the order is submitted.
	 * Only shown on the new tab flow: adding to an existing check and modifying an order
	 * both pass no owner, so the app reports the logged in operator rather than whoever
	 * the check actually belongs to.
	 *
	 * @return {string}
	 */
	private get tabOwnerSubtitle(): string {
		if (this.modifyingOrder || this.orderDetails?.checkNum || !this.employeeName) {
			return '';
		}

		return this.$t('orders.tab_owner', { name: this.employeeName });
	}

	private get canAddOpenItem(): boolean {
		return this.hasPermission(PERMISSION.ORDER_ADD_OPEN_ITEM) && this.isCateringRestaurant;
	}

	private get isUserFromAPP(): boolean {
		return !!window.B2BApp;
	}

	private get hasAccessToMemberMenus(): boolean {
		return this.checkout.memberInfo?.provider !== MEMBERSHIP_PROVIDERS.SPH;
	}

	/**
	 * Gets featured items from ALL menus
	 *
	 * @return {MenuItem[]}
	 */
	private get featuredItems(): MenuItem[] {
		const allItems: MenuItem[] = [];

		// Get items from all menus
		this.menus.forEach(menu => {
			if (menu?.sections?.length) {
				menu.sections.forEach(section => {
					if (section.items) {
						allItems.push(...section.items);
					}
				});
			}
			else if (menu.items) {
				allItems.push(...menu.items);
			}
		});

		return allItems.filter(item => item.featured_item && !item.sold_out && !item.unavailable);
	}

	private get shouldShowQueueBanner(): boolean {
		const size = Number(this.queueInfo?.queueSize);
		const mins = this.queueInfo?.minutesLeft;
		return Number.isFinite(size) && size >= 0 && mins !== undefined && mins !== null;
	}

	private get menuBanner(): string {
		return this.$tc('menu.queue_banner', this.queueInfo?.queueSize, {
			count: this.queueInfo?.queueSize,
			minutes: this.queueInfo?.minutesLeft
		});
	}

	private async created(): Promise<void> {
		this.resetMenuSelection();
		if(this.modifyingOrder && !this.kioskMode) {
			this.title = this.$t('orders.modify_header');
		}
		else if(this.isUserFromAPP && !this.kioskMode) {
			if(this.orderDetails?.checkNum) {
				this.title = this.$t('orders.add_to_check_header');
				this.warningModalTitle = this.$t('orders.warning_modal.add_to_check.title');
				this.warningModalBody = this.$t('orders.warning_modal.add_to_check.text');
			}
			else {
				this.title = this.$t('orders.new_check_header');
				this.warningModalTitle = this.$t('orders.warning_modal.new_check.title');
				this.warningModalBody = this.$t('orders.warning_modal.new_check.text');
			}
		}
		else {
			this.title = this.isQuoting ? this.$t('orders.create_quote_header') : this.$t('orders.create_header');
		}
		// Populate order queue time for pizzabot locations
		if (this.isPizzabotLocation) {
			await this.fetchNextOrderQueueTime();
		}

		// Set up interval to fetch orders paused status every 30 seconds (only in kiosk mode)
		if (this.kioskMode) {
			this.ordersPausedInterval = window.setInterval(async () => {
				await this.fetchRestaurant({ tenantId: this.restaurant.id });
			}, 30000);
		}
	}

	/**
	 * For some reason order-menu-sections.offsetHeight is 0 (except for the first tab-item) if we implement it in the OrderMenu's scrollToSection,
	 * so we need to store it here on first load.
	 * However if the very first menu doesn't have more than 1 section, the height would be 0.
	 * That's why we set a default hard-coded value of 48px
	 * ! If we ever update the height of the section headers v-tabs-bar, we need to update this default value
	 */
	private mounted(): void {
		this.sectionsHeaderHeight = document.getElementById('order-menu-sections')?.offsetHeight || 48;
		window.addEventListener('scroll', this.handleScrollOnChild);
	}

	private beforeDestroy(): void {
		cancelDebounce(this.searchItems);
		window.removeEventListener('scroll', this.handleScrollOnChild);

		// Clear the orders paused interval
		if (this.ordersPausedInterval) {
			clearInterval(this.ordersPausedInterval);
		}
	}

	private backToApp(): void {
		window.B2BApp.backToApp();
	}

	private potentialBackToApp(): void {
		this.cartItemsCount ? this.showWarningModal = true : this.backToApp();
	}

	/**
	 * On scroll event, check if the user has scrolled more than 200px, if so, show the scroll to top button
	 *
	 * @param {any} e
	 * @return {void}
	 */
	private onScroll(e: any): void {
		const max: number = 200;
		const top: number = window.pageYOffset || e.target.scrollTop || 0;
		this.showScrollToTopButton = top > max;
	}

	/**
	 * Scroll to the top of the page
	 * and manually set the selectedSection to 0 in the children components as it sometimes doesn't by itself when switching menus
	 *
	 * @param {boolean} isInstant - Is there a scroll animation or is it instant
	 * @return {void}
	 */
	private scrollToTop(isInstant: boolean): void {
		this.$vuetify.goTo(0, isInstant ? { duration: 0 } : undefined);

		const orderMenuRefs = this.$refs.orderMenu;
		if (Array.isArray(orderMenuRefs)) {
			orderMenuRefs.forEach((orderMenu) => {
				if (orderMenu) {
					orderMenu.selectedSection = 0;
				}
			});
		}
	}

	/**
	 * Toggle the scroll listener on the window (when an OrderMenu scroll to a section)
	 *
	 * @param {boolean} addEvent
	 */
	private toggleScrollListener(addEvent: boolean): void {
		addEvent ? window.addEventListener('scroll', this.handleScrollOnChild) : window.removeEventListener('scroll', this.handleScrollOnChild);
	}

	/**
	 * Listen to the scroll event and set the selected menu or section based on Y axis
	 *
	 * @return {void}
	 */
	private handleScrollOnChild(): void {
		const activeMenu = this.$refs.orderMenu[this.menuSelectedIndex];
		if (activeMenu?.handleScroll) {
			activeMenu.handleScroll();
		}
	}

	/**
	 * List all menu/section items, filtered by order type to avoid SKU
	 * collisions between event and retail items.
	 *
	 * @return {MenuItem[]}
	 */
	private get allItems(): MenuItem[] {
		const isEventOrder = this.orderDetails?.menuManagerEvent === true;
		const items: MenuItem[] = [];

		this.menus
			.filter(menu => menu.event_package === isEventOrder)
			.forEach(menu => {
				if (menu?.sections) {
					menu.sections.forEach(section => {
						if (section.items) {
							items.push(...section.items);
						}
					});
				}
				else if (menu?.items) {
					items.push(...menu.items);
				}
			});

		return items;
	}

	/**
	 * Toggle the mobile search bar.
	 * If we close the search bar, empty the input.
	 *
	 * @return {void}
	 */
	private toggleMobileSearch(): void {
		this.showMobileSearch = !this.showMobileSearch;

		if (!this.showMobileSearch) {
			this.searchInput = '';
		}
	}

	/**
	 * Search items based on the search input
	 *
	 * @return {void}
	 */
	@Debounce(500)
	private searchItems(): void {
		if (this.searchInput !== '') {
			this.searchResultItems = this.allItems.filter((item: any) =>
				['name', 'description'].some(field => {
					const fieldValue: string = localize(item, field, this.$i18n.locale, this.restaurant.locales);
					return fieldValue != null && fieldValue.toString().toLowerCase().includes(this.searchInput.toLowerCase());
				})
			);
		}
		else {
			this.searchResultItems = [];
		}
	}

	private onMenuChange(newIndex: number): void {
		// Prevents invalid index
		if (newIndex < 0 || newIndex >= this.menus.length) {
			return;
		}

		// Guard for member-only menus
		if (this.menus[newIndex].member_only && !this.checkout.memberInfo?.identifier) {
			this.showMembershipModal = true;
			return;
		}

		this.menuSelectedIndex = newIndex;
		this.$nextTick(() => this.scrollToTop(true));
	}

	/**
	 * Close the membership modal and reset the menu selection.
	 *
	 * @return {void}
	 */
	private cancelShowingMemberMenu(): void {
		this.showMembershipModal = false;
		this.resetMenuSelection(this.prevMenuSelectedIndex);
		this.$nextTick(() => this.scrollToTop(true));
	}

	/**
	 * Select the first non-member-only menu if the user doesn't have access to member menus.
	 *
	 * @param {number} [targetMenuIndex]
	 * @return {void}
	 */
	private resetMenuSelection(targetMenuIndex?: number): void {
		if (targetMenuIndex !== undefined) {
			this.menuSelectedIndex = targetMenuIndex;
			this.prevMenuSelectedIndex = targetMenuIndex;
			return;
		}
		this.menuSelectedIndex = (!this.checkout.memberInfo?.identifier || !this.hasAccessToMemberMenus) ? this.menus.findIndex(menu => !menu.member_only) : 0;
		this.prevMenuSelectedIndex = this.menuSelectedIndex;
	}
}
